'use client';

import React from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Location } from './types';
import LocationCard from './LocationCard';
import { useLocationCarousel } from './useLocationCarousel';

const SlidingTrack = styled(Box)({
    display: 'flex',
    width: '100%',
    transition: 'transform 500ms ease-in-out',
});

interface LocationCarouselProps {
    locations: Location[];
    carousel: ReturnType<typeof useLocationCarousel>;
}

export default function LocationCarousel({ locations, carousel }: LocationCarouselProps) {
    const {
        currentSlide,
        containerRef,
        containerWidth,
        handleMouseEnter,
        handleMouseLeave,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
    } = carousel;

    const getGapPx = (): number => {
        if (!containerRef.current) return 0;
        const val = getComputedStyle(containerRef.current)
            .getPropertyValue('--carousel-gap')
            .trim();
        return parseFloat(val) || 0;
    };

    const gapPx = containerWidth ? getGapPx() : 0;

    return (
        <Box
            ref={containerRef}
            sx={{
                overflow: 'hidden',
                width: '100%',
                position: 'relative',
                containerType: 'inline-size',
                '--cards-per-slide': 1,
                '--carousel-gap': '0px',
                '@media (min-width: 768px)': {
                    '--cards-per-slide': 2,
                    '--carousel-gap': '24px',
                },
                '@media (min-width: 1200px)': {
                    '--cards-per-slide': 3,
                    '--carousel-gap': '32px',
                },
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <SlidingTrack
                sx={{
                    gap: 'var(--carousel-gap)',
                    transform: containerWidth
                        ? `translateX(-${currentSlide * (containerWidth + gapPx)}px)`
                        : 'translateX(0)',
                }}
            >
                {locations.map((location) => (
                    <LocationCard key={location.id} location={location} />
                ))}
            </SlidingTrack>
        </Box>
    );
}
